import { Button, Text, Title, UnstyledButton } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import {
  submitFeatureSurvey,
  SurveyBetaOptin,
  SurveyInterest,
  SurveyUrgency,
} from 'src/Services/Hubspot/featureSurvey';
import { trackFeatureSurveyAnswered } from 'src/Services/Mixpanel/events';

import styles from './FeatureSurvey.module.scss';
import { Props } from './FeatureSurvey.types';

interface SurveyState {
  interest?: SurveyInterest;
  betaOptin?: SurveyBetaOptin;
  urgency?: SurveyUrgency;
}

const STORAGE_PREFIX = 'feature_survey_';

const loadState = (moduleKey: string): SurveyState => {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${moduleKey}`);
    if (!raw) return {};
    return JSON.parse(raw) as SurveyState;
  } catch {
    return {};
  }
};

const saveState = (moduleKey: string, state: SurveyState): void => {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${moduleKey}`, JSON.stringify(state));
  } catch {
    // ignore quota/serialization errors — the answer stays in memory for this session
  }
};

const countAnswers = (state: SurveyState): number =>
  (state.interest ? 1 : 0) + (state.betaOptin ? 1 : 0) + (state.urgency ? 1 : 0);

const FeatureSurvey = ({ moduleKey }: Props) => {
  const { t } = useTranslation();
  const { data: user } = useFetchUser();
  const [state, setState] = useState<SurveyState>(() => loadState(moduleKey));

  useEffect(() => {
    setState(loadState(moduleKey));
  }, [moduleKey]);

  const bugReportUrl = import.meta.env.VITE_BUG_REPORT_URL;
  const answered = countAnswers(state);

  const interestOptions = useMemo<{ value: SurveyInterest; label: string }[]>(
    () => [
      { value: 'oui', label: t('featurePreview.survey.q1_oui') },
      { value: 'peut-etre', label: t('featurePreview.survey.q1_peutEtre') },
      { value: 'non', label: t('featurePreview.survey.q1_non') },
    ],
    [t]
  );

  const betaOptions = useMemo<{ value: SurveyBetaOptin; label: string }[]>(
    () => [
      { value: 'oui', label: t('featurePreview.survey.q2_oui') },
      { value: 'non', label: t('featurePreview.survey.q2_non') },
    ],
    [t]
  );

  const urgencyOptions = useMemo<{ value: SurveyUrgency; label: string }[]>(
    () => [
      { value: '1_mois', label: t('featurePreview.survey.q3_1mois') },
      { value: '3_mois', label: t('featurePreview.survey.q3_3mois') },
      { value: '6_mois', label: t('featurePreview.survey.q3_6mois') },
    ],
    [t]
  );

  const handleAnswer = <K extends keyof SurveyState>(
    question: K,
    value: NonNullable<SurveyState[K]>,
    mixpanelQuestion: 'interest' | 'beta_optin' | 'urgency'
  ) => {
    const next: SurveyState = { ...state, [question]: value };
    setState(next);
    saveState(moduleKey, next);
    trackFeatureSurveyAnswered({ module: moduleKey, question: mixpanelQuestion, answer: value });
    submitFeatureSurvey({
      email: user?.email,
      moduleKey,
      interest: next.interest,
      betaOptin: next.betaOptin,
      urgency: next.urgency,
    });
  };

  const renderOption = <T extends string>(
    option: { value: T; label: string },
    isSelected: boolean,
    onPick: (value: T) => void
  ) => (
    <UnstyledButton
      key={option.value}
      className={`${styles.option} ${isSelected ? styles.selected : ''}`}
      onClick={() => onPick(option.value)}
      aria-pressed={isSelected}
    >
      {option.label}
    </UnstyledButton>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title order={3}>{t('featurePreview.survey.title')}</Title>
        <Text className={styles.intro}>{t('featurePreview.survey.intro')}</Text>
      </div>

      <div className={styles.question}>
        <Text className={styles.questionLabel}>{t('featurePreview.survey.q1')}</Text>
        <div className={styles.optionsRow}>
          {interestOptions.map(option =>
            renderOption(option, state.interest === option.value, value =>
              handleAnswer('interest', value, 'interest')
            )
          )}
        </div>
      </div>

      <div className={styles.question}>
        <Text className={styles.questionLabel}>{t('featurePreview.survey.q2')}</Text>
        <div className={styles.optionsRow}>
          {betaOptions.map(option =>
            renderOption(option, state.betaOptin === option.value, value =>
              handleAnswer('betaOptin', value, 'beta_optin')
            )
          )}
        </div>
      </div>

      <div className={styles.question}>
        <Text className={styles.questionLabel}>{t('featurePreview.survey.q3')}</Text>
        <div className={styles.optionsRow}>
          {urgencyOptions.map(option =>
            renderOption(option, state.urgency === option.value, value =>
              handleAnswer('urgency', value, 'urgency')
            )
          )}
        </div>
      </div>

      <div className={styles.footer}>
        {answered > 0 ? (
          <Text size="sm" className={styles.progress}>
            {t('featurePreview.survey.progress', { count: answered })}
          </Text>
        ) : (
          <span />
        )}
        {bugReportUrl && (
          <Button variant="outline" onClick={() => window.open(bugReportUrl, '_blank')}>
            {t('featurePreview.survey.feedbackCta')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FeatureSurvey;
